import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { PrismTooltipHeader } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { gt, lt } from 'ember-truth-helpers';
import {
  COLOR_CHART_STRATEGY_COMPETITOR,
  COLOR_CHART_STRATEGY_OWN_HOTEL,
  getCssVariableValue,
} from 'frontend-utils/utils/css-variable';
import HotelStrategySubtypeHeader from 'lumen/components/hotel/strategy/subtype-header';
import {
  REFERENCE_PERIODS,
  RSTYPES_IN_CANCELLATION_POLICY_SECTION,
  SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
  SUBTYPE_VALUES_KEY_STRATEGY,
} from 'lumen/utils/constants/rate-strategy-constants';

interface HotelStrategyTooltipsSubtypeTooltipContentSignature {
  Args: {
    dataToShow: unknown;
    subTypeHeaderLabel: unknown;
    subtype: unknown;
  };
}

export default class HotelStrategyTooltipsSubtypeTooltipContent extends Component<HotelStrategyTooltipsSubtypeTooltipContentSignature> {
  @service declare intl: Services['intl'];
  @service declare rsSelections: Services['rs-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get periodFromParams() {
    if (
      RSTYPES_IN_CANCELLATION_POLICY_SECTION.includes(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.args.subtype.rstype.name,
      )
    ) {
      // @ts-expect-error: Incorrect type
      return this.referencePeriodKeys.find(
        (referencePeriod) =>
          referencePeriod.id === this.referencePeriodCancellationPolicySection,
      ).name;
    }
    // @ts-expect-error: Incorrect type
    return this.referencePeriodKeys.find(
      (referencePeriod) =>
        referencePeriod.id === this.referencePeriodStrategySection,
    ).name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodCancellationPolicySection() {
    return this.rsSelections?.referencePeriodCancellationPolicySection;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodKeys() {
    return [
      {
        id: REFERENCE_PERIODS.ONE_WEEK_AGO,
        name: this.intl.t('general.weeksago', { count: 1 }),
      },
      {
        id: REFERENCE_PERIODS.ONE_MONTH_AGO,
        name: this.intl.t('general.monthsago', { count: 1 }),
      },
      {
        id: REFERENCE_PERIODS.THREE_MONTHS_AGO,
        name: this.intl.t('general.monthsago', { count: 3 }),
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodStrategySection() {
    return this.rsSelections?.referencePeriodStrategySection;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipRows() {
    if (this.args.dataToShow === SUBTYPE_VALUES_KEY_STRATEGY) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      return [...this.args.subtype.filteredStrategies]
        .filter(
          (strategy) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !strategy.isMissing &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !strategy.isNonComputable &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !strategy.isNotOffered,
        )
        .map((strategy) => {
          const mappedValues = {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            supplementOrDiscount: strategy.supplementOrDiscount,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            changeOverTime: strategy.changeOverTime,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            formattedValue: strategy.formattedValue,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            formattedHistoricValue: strategy.formattedHistoricValue,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            formattedChangeOverTime: strategy.formattedChangeOverTime,
            backgroundColor: htmlSafe(
              `background-color: ${
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                strategy.isOwnHotel
                  ? getCssVariableValue(COLOR_CHART_STRATEGY_OWN_HOTEL)
                  : getCssVariableValue(COLOR_CHART_STRATEGY_COMPETITOR)
              }`,
            ),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            isOwnHotel: strategy.isOwnHotel,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            hotelName: strategy.hotelinfo.name,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            isSoldOut: strategy.isSoldOut,
          };

          return mappedValues;
        })
        .sort((strategyA, strategyB) => {
          if (strategyA.isSoldOut) {
            return 1;
          }
          if (strategyB.isSoldOut) {
            return -1;
          }

          // @ts-expect-error: Incorrect type
          if (this.args.subtype.isPosType) {
            // POS strategies are sorted in ascending order
            return (
              strategyA.supplementOrDiscount - strategyB.supplementOrDiscount
            );
          }

          return (
            strategyB.supplementOrDiscount - strategyA.supplementOrDiscount
          );
        });
    }

    if (this.args.dataToShow === SUBTYPE_VALUES_KEY_CANCELLATION_POLICY) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return (
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.subtype.filteredCancellationpolicies
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter(
            // @ts-expect-error: Incorrect type
            (cancellationPolicy) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !cancellationPolicy.isMissing &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !cancellationPolicy.isNonComputable &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !cancellationPolicy.isNotOffered,
          )
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .map((cancellationPolicy) => {
            const mappedValues = {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              days: cancellationPolicy.days,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              time: cancellationPolicy.time,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              changeOverTime: cancellationPolicy.changeOverTime,
              backgroundColor: htmlSafe(
                `background-color: ${
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  cancellationPolicy.isOwnHotel
                    ? getCssVariableValue(COLOR_CHART_STRATEGY_OWN_HOTEL)
                    : getCssVariableValue(COLOR_CHART_STRATEGY_COMPETITOR)
                }`,
              ),
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              formattedValue: cancellationPolicy.formattedValueWithDetails,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              formattedHistoricValue:
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                cancellationPolicy.formattedHistoricValueWithDetails,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              formattedChangeOverTime:
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                cancellationPolicy.formattedChangeOverTime,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              hotelName: cancellationPolicy.hotelinfo.name,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              isSoldOut: cancellationPolicy.isSoldOut,
            };

            return mappedValues;
          })
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .sort((cancellationPolicyA, cancellationPolicyB) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (cancellationPolicyA.isSoldOut) {
              return 1;
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (cancellationPolicyB.isSoldOut) {
              return -1;
            }

            // Date should be sorted in ascending order
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (cancellationPolicyA.days === cancellationPolicyB.days) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (cancellationPolicyA.time && cancellationPolicyB.time) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                const timeNumberA = cancellationPolicyA.time.split(':')[0];
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                const timeNumberB = cancellationPolicyB.time.split(':')[0];

                // Time should be sorted in descending order
                // https://otainsight.slack.com/archives/G01BUCJAFCN/p1645184313844719?thread_ts=1633075719.014900&cid=G01BUCJAFCNx
                return timeNumberB - timeNumberA;
              }

              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (cancellationPolicyA.time) {
                return 1;
              }

              return -1;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            return cancellationPolicyA.days - cancellationPolicyB.days;
          })
      );
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipTitle() {
    let definition = this.intl.t('strategy.metric_block.discount');

    if (this.args.dataToShow === SUBTYPE_VALUES_KEY_CANCELLATION_POLICY) {
      definition = this.intl.t('strategy.metric_block.cutoff_policy');
    }

    // @ts-expect-error: Incorrect type
    if (this.args.subtype.isMealtypeType) {
      definition = this.intl.t('strategy.mealtype.supplement');
    }

    // @ts-expect-error: Incorrect type
    if (this.args.subtype.isPosType) {
      definition = this.intl.t('general.difference');
    }

    return definition.toLowerCase();
  }

  <template>
    <PrismTooltipHeader>
      <div class="text-300--regular flex flex-row items-center justify-center">
        {{! @glint-expect-error: Incorrect type }}
        {{#if @subtype.isCancellationPolicyType}}
          {{! @glint-expect-error: Incorrect type }}
          <p>{{@subtype.rstype.formattedTypeName}}</p>
        {{else}}
          {{! @glint-expect-error: Incorrect type }}
          <HotelStrategySubtypeHeader
            @formattedLabel={{@subTypeHeaderLabel}}
            {{! @glint-expect-error: Incorrect type }}
            @isBranddotcomMemberRatesSubType={{@subtype.isBranddotcomMemberRatesSubType}}
            {{! @glint-expect-error: Incorrect type }}
            @isMealtypeType={{@subtype.isMealtypeType}}
            {{! @glint-expect-error: Incorrect type }}
            @isMemberRatesType={{@subtype.isMemberRatesType}}
            {{! @glint-expect-error: Incorrect type }}
            @isMobileBookingSubtype={{@subtype.isMobileBookingSubtype}}
            {{! @glint-expect-error: Incorrect type }}
            @isMobileExpediaSubtype={{@subtype.ismobileExpediaSubtype}}
            {{! @glint-expect-error: Incorrect type }}
            @isPosBookingSubtype={{@subtype.isPosBookingSubtype}}
            {{! @glint-expect-error: Incorrect type }}
            @isPosExpediaSubtype={{@subtype.isPosExpediaSubtype}}
            {{! @glint-expect-error: Incorrect type }}
            @label={{@subtype.label}}
          />
        {{/if}}
        <p class="ml-100">{{this.tooltipTitle}}</p>
      </div>
    </PrismTooltipHeader>
    <table class="default-table">
      <tbody>
        <tr>
          <td class="text-200--bold">
            {{t "general.hotel.plural" count=1}}
          </td>
          <td>
            <div class="text-200--bold flex justify-end">
              {{t "general.today"}}
            </div>
          </td>
          <td>
            <div class="text-200--bold flex justify-end">
              {{this.periodFromParams}}
            </div>

          </td>
          <td>
            <div class="text-200--bold flex justify-end">
              {{t "rates.changes.change_tooltip"}}
            </div>

          </td>
        </tr>
        {{#each this.tooltipRows as |rowData|}}
          <tr class={{if rowData.isOwnHotel "highlight"}}>
            <td>
              <div class="rate-strategy-tooltip-cell-width-limit truncate">
                <div
                  class="identifier-bullet"
                  style={{rowData.backgroundColor}}
                ></div>{{rowData.hotelName}}
              </div>
            </td>
            <td>
              <div class="flex justify-end">
                {{rowData.formattedValue}}
              </div>
            </td>
            <td>
              <div class="flex justify-end">
                {{rowData.formattedHistoricValue}}
              </div>
            </td>
            <td>
              <div
                class="flex justify-end
                  {{if (gt rowData.changeOverTime 0) 'text-success-default'}}
                  {{if (lt rowData.changeOverTime 0) 'text-critical-default'}}"
              >
                {{rowData.formattedChangeOverTime}}
              </div>
            </td>
          </tr>
        {{/each}}
      </tbody>
    </table>
  </template>
}
